/* eslint-disable react/jsx-no-useless-fragment */
import React, { useEffect } from 'react';

import { useRecoilState } from 'recoil';
import styled, { css } from 'styled-components';

import { Button } from 'src/app/components/lib/button';

import { WorkflowCampaignBuilderSummarySidebar } from '../../summary-sidebar';

import { emailEditorNavigationState, isEmailEditorEnabled } from './atoms/workflow-and-email-editor-atoms';
import { BeefreeEditorBuilder } from './beefree-editor';
import { ReactFlowEditor } from './reactflow-editor';
import { WorkflowsTemplateSelect } from './template-select/workflow-template-select';

import type { WizardStepComponentProps } from 'src/app/components/lib/wizard';

export function WorkflowEditor({
  enableCompleteButton,
  disableCompleteButton,
  hideBottomNavigation,
  showBottomNavigation,
}: WizardStepComponentProps) {
  const [showEditor] = useRecoilState(isEmailEditorEnabled);
  const [navState, setNavState] = useRecoilState(emailEditorNavigationState);
  const { currentStep, isNextButtonEnabled, isEditorLoading } = navState;

  useEffect(() => {
    if (navState.hideWizardBottomNavigation) {
      hideBottomNavigation();
    } else {
      showBottomNavigation();
    }
  }, [navState.hideWizardBottomNavigation, hideBottomNavigation, showBottomNavigation]);

  const handleNextStep = async () => {
    setNavState((prev) => ({
      ...prev,
      currentStep: 1,
    }));
  };

  const handlePreviousStep = async () => {
    setNavState((prev) => ({
      ...prev,
      currentStep: 0,
    }));
  };

  return (
    <>
      {showEditor ? (
        <EmailEditorContainer>
          {currentStep === 0 ? (
            <TemplateSelectContainer>
              <WorkflowsTemplateSelect />
            </TemplateSelectContainer>
          ) : (
            <BeefreeEditorBuilder />
          )}
          <NavigationContainer $isNextButton={currentStep === 0}>
            {currentStep === 1 && (
              <Button
                disabled={!isNextButtonEnabled || isEditorLoading}
                label='Back'
                variant='secondary'
                onClick={async () => {
                  await handlePreviousStep();
                }}
              />
            )}
            {currentStep === 0 && (
              <Button
                disabled={!isNextButtonEnabled || isEditorLoading}
                label='Next'
                onClick={async () => {
                  await handleNextStep();
                }}
              />
            )}
          </NavigationContainer>
        </EmailEditorContainer>
      ) : (
        <ReactFlowEditorContainer>
          <ReactFlowEditor disableCompleteButton={disableCompleteButton} enableCompleteButton={enableCompleteButton} />
          <WorkflowCampaignBuilderSummarySidebar />
        </ReactFlowEditorContainer>
      )}
    </>
  );
}

const ReactFlowEditorContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
`;

const EmailEditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const TemplateSelectContainer = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const NavigationContainer = styled.div<{ $isHeader?: boolean; $isNextButton: boolean; $singleButton?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: ${({ $isNextButton }) => ($isNextButton ? 'flex-end' : 'flex-start')};
  padding: var(--sizes-30) var(--sizes-60);
  background: var(--color-brand-primary-white);
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.05);
  ${({ $isHeader }) =>
    $isHeader &&
    css`
      border-bottom: 1px solid var(--color-gray-20);
    `}
`;
